import { Speaker, Play, Pause, Download } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../firebase/AuthContext';
import { useFirebase } from '../firebase/FirebaseContext';
import { collection, addDoc, getDoc, query, where, orderBy, onSnapshot, limit, serverTimestamp, doc, writeBatch, updateDoc, getDocs } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import AudioStream from './AudioElement';
import VoiceSidebar from './RightBar';

import { Select } from "@chakra-ui/react";
// import { collection, query, orderBy, , onSnapshot } from 'firebase/firestore';
import ServerControl from '../ServerComponent/ServerControl';
import CustomAudioPlayer from './CustomAudioPlayer';


const voices = [
  { id: '1', name: 'Original', is_voice: false, default_inference_parameters:  { temperature: 1.0, topP: 1.0, repetitionPenalty: 1.5 } },
  { id: '2', name: '22/05', is_voice: true, default_inference_parameters: { temperature: 0.6, topP: 0.9, repetitionPenalty: 1.15 } },
];

const suggestionButtons = [
  // 'Narrate a story',
  // 'Tell a silly joke',
  // 'Record an advertisement',
  // 'Speak in different languages',
  // 'Direct a dramatic movie scene',
  // 'Hear from a video game character',
  // 'Introduce your podcast',
  // 'Guide a meditation class',
];

const TTSPlayground = () => {
  const { user } = useAuth();
  const { db } = useFirebase();
  const [isVoiceDropdownOpen, setIsVoiceDropdownOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [audioElement, setAudioElement] = useState(null);
  const [temperature, setTemperature] = useState(1.0);
  const [topP, setTopP] = useState(1.0);
  const [repetitionPenalty, setRepetitionPenalty] = useState(1.5);
  const [audioUrl, setAudioUrl] = useState(null);
  const [sampleHistory, setSampleHistory] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(voices[0]);

  const [podId, setPodId] = useState(null)
  const [serverStatus, setStatus] = useState(null)

  useEffect(() => {
    if (!user?.orgId) return
    const orgRef = doc(db, 'organisations', user.orgId)

    getDoc(orgRef).then(snap => {
      if (snap.exists()) console.log('orgData', snap.data())
    })

    const unsubOrg = onSnapshot(orgRef, snap => {
      if (snap.exists()) {
        setPodId(snap.data().activeSessionId || null)
      }
    })

    return () => unsubOrg()
  }, [user, db])

  useEffect(() => {
    // only run once you have a user and an orgId
    if (!user?.uid || !user.orgId) return;
    console.log("listening for org status on", user.orgId);

    const statusRef = doc(db, "organisations", user.orgId);
    const unsubscribe = onSnapshot(
      statusRef,
      (snap) => {
        if (!snap.exists()) {
          console.log("org doc not found");
          setStatus(null);
          return;
        }

        const s = snap.data().status;
        console.log("org status:", s, (s === "active"));
        // true only if status === "active", else false
        if (s === "active") {
          setStatus("running");
        }
        else if (s === "inactive") {
          setStatus("stopped");
        }
        else if (s === "deploying") {
          setStatus("loading");
        }

      },
      (err) => console.error("status listener error", err)
    );

    return () => unsubscribe();
  }, [user, podId]);

  useEffect(() => {
    if (!user?.uid) return;

    const gensRef = collection(db, 'users', user.uid, 'playgroundGenerations');
    const q = query(
      gensRef,
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const jobsData = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();

            // Convert Firestore Timestamps to JS Dates
            const createdAt = data.createdAt?.toDate();
            const completedAt = data.completedAt?.toDate();

            // Resolve audio URL (if it’s a storage path rather than a full URL)
            let audioUrl = data.audioFileUrl;
            if (audioUrl && !audioUrl.startsWith('http')) {
              try {
                const storage = getStorage();
                const fileRef = ref(storage, audioUrl);
                audioUrl = await getDownloadURL(fileRef);
              } catch (e) {
                console.error('Failed to fetch download URL for', audioUrl, e);
              }
            }

            return {
              id: docSnap.id,
              text: data.text,
              status: data.status,
              createdAt,
              completedAt,
              audioUrl,
              duration: data.duration,
              frames: data.frames,
              sampleRate: data.sampleRate,
              temperature: data.temperature,
              topP: data.topP,
              voiceId: data.voiceId,
            };
          })
        );

        console.log('Latest 20 generations:', jobsData);
        setJobs(jobsData);
        setLoading(false);
      },
      (err) => console.error('playgroundGenerations listener error:', err)
    );

    return () => unsubscribe();
  }, [user?.uid, db]);

  useEffect(() => {
    console.log("user", user.orgId);
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
      }
    };
  }, [audioElement]);

  useEffect(() => {
    if (!user?.uid) return;

    // reference the sub-collection
    const gensRef = collection(db, 'users', user.uid, 'playgroundGenerations');
    // build a query: order by createdAt desc, limit to 20
    const q = query(gensRef, orderBy('createdAt', 'desc'), limit(10));

    // subscribe
    const unsubscribe = onSnapshot(q, snapshot => {
      const latest10 = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log('Latest 20 generations:', latest10);
      setJobs(latest10);        // if you also want to update state
    }, err => {
      console.error('Listen failed:', err);
    });

    // cleanup on unmount or deps change
    return () => unsubscribe();
  }, [user?.uid, db]);

  const handleGenerateSpeech = async () => {
    if (!inputText.trim()) return;

    try {
      const text = inputText.trim();

      // Create the generation object
      const generation = {
        text,
        voiceId: selectedVoice.id,
        status: 'pending',
        createdAt: new Date(),
        topP,
        temperature,
      };

      // Add to local state immediately


      // Ping the server with the generation request
      try {
        const requestBody = {
          prompt: text,
          voiceId: generation.voiceId,
          userId: user.uid,
          topP: generation.topP,
          temperature: generation.temperature,
          voice: "tara",
        };
        console.log('Sending to server:', requestBody);
        let audioUrl = null;
        if (selectedVoice.is_voice) {
          audioUrl = `https://api.canopylabs.ai/tts-proxy?text=${encodeURIComponent(text)}&topP=${generation.topP}&temperature=${generation.temperature}&userId=${encodeURIComponent(user.uid)}&voice=hatsu&model_id=${selectedVoice.id}`;
        } else {
          audioUrl = `https://api.canopylabs.ai/tts-proxy?text=${encodeURIComponent(text)}&topP=${generation.topP}&temperature=${generation.temperature}&userId=${encodeURIComponent(user.uid)}&model_id=${selectedVoice.id}`;
        }

        console.log("audioUrl", audioUrl);

        setAudioUrl(audioUrl);
        // Update the generation with the audio URL
        setJobs(prevJobs =>
          prevJobs.map(job =>
            job === generation
              ? { ...job, status: 'completed', audioUrl }
              : job
          )
        );

        // Set the input text to trigger AudioStream playback
        setInputText(text);

      } catch (error) {
        console.error('Error notifying server:', error);
        // Update the generation status to failed
        setJobs(prevJobs =>
          prevJobs.map(job =>
            job === generation
              ? { ...job, status: 'failed' }
              : job
          )
        );
      }

      // Clear input
      // setInputText('');
    } catch (error) {
      console.error('Error creating generation:', error);
    }
  };


  return (
    <div className="flex flex-col h-full h-[calc(100vh)] overflow-hidden ">
      {/* Header */}
      <div className="py-4 pl-5 border-b border-gray-200 flex items-center justify-between mr-2">
        <div className="text-sm font-medium text-gray-800">Text to Speech</div>
        <ServerControl />
      </div>

      {/* Content Area */}
      <div className="flex flex-col md:flex-row h-full">
        {/* Left: Text Input */}
        <div className="flex-1 flex flex-col p-6 h-full">
          <div className="flex-1 flex flex-col min-h-0">
            <textarea
              className="w-full h-full px-4 py-3 rounded-md border-gray-300 focus:outline-none text-base resize-none mb-4"
              placeholder="Start typing here or paste any text you want to turn into speech..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
            />
          </div>

          {/* Generate Button */}

          <div className="mb-4 flex justify-end items-center space-x-4">
            {audioUrl && <CustomAudioPlayer audioUrl={audioUrl} />}
            <button
              onClick={handleGenerateSpeech}
              disabled={serverStatus !== 'running'}
              className={`text-sm py-2 px-6 rounded h-10 flex items-center justify-center ${serverStatus === 'running'
                  ? 'bg-black text-white hover:bg-gray-800 hover:scale-105 active:scale-95'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                } transition-all duration-150`}
            >
              Generate
            </button>
          </div>



          {/* Audio Stream Component */}
          <AudioStream
            text={inputText}
            temperature={temperature}
            topP={topP}
            repetitionPenalty={repetitionPenalty}
          />
        </div>

        {/* Divider between main content and sidebar */}
        <div className="hidden md:block w-px bg-gray-200 h-full"></div>

        {/* Right: Settings Sidebar */}
        <VoiceSidebar
          topP={topP}
          setTopP={setTopP}
          repetitionPenalty={repetitionPenalty}
          setRepetitionPenalty={setRepetitionPenalty}
          temperature={temperature}
          setTemperature={setTemperature}
          sampleHistory={jobs}
          voices={voices}
          selectedVoice={selectedVoice}
          setSelectedVoice={setSelectedVoice}
        />
      </div>
    </div>
  );
};

export default TTSPlayground;